import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Upload, Sparkles, ArrowRight, Loader2, MapPin, ChevronRight, Star } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import Logo from '@/components/Logo';
import { frictionlessApi } from '@/services/api/frictionlessApi';
import { frictionlessTempApi } from '@/services/api/frictionlessTempApi';
import { debounce } from 'lodash';
import Cookies from 'js-cookie';

const RestaurantMenuSearch = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse restaurant name and location from search query
  const parseSearchQuery = (searchQuery: string): { restaurant: string; location: string | null } => {
    // Check for comma separator (e.g., "J Alexanders, Livonia")
    if (searchQuery.includes(',')) {
      const parts = searchQuery.split(',').map(s => s.trim());
      if (parts.length >= 2) {
        const restaurant = parts[0];
        const location = parts.slice(1).join(', '); // Handle "City, State" format
        return { restaurant, location };
      }
    }
    
    // Check for state abbreviation at end (e.g., "J Alexanders Nashville TN")
    const statePattern = /\s+([A-Z]{2})$/;
    const stateMatch = searchQuery.match(statePattern);
    if (stateMatch) {
      const restaurant = searchQuery.replace(statePattern, '').trim();
      return { restaurant, location: stateMatch[0].trim() };
    }
    
    // Check for zip code (e.g., "J Alexanders 48152")
    const zipPattern = /\s+(\d{5})$/;
    const zipMatch = searchQuery.match(zipPattern);
    if (zipMatch) {
      const restaurant = searchQuery.replace(zipPattern, '').trim();
      return { restaurant, location: zipMatch[1] };
    }
    
    // No location found, return as is
    return { restaurant: searchQuery, location: null };
  };

  useEffect(() => {
    // Auto-focus on mount
    inputRef.current?.focus();
  }, []);

  // Google Places Autocomplete for accurate restaurant suggestions
  const fetchSuggestions = useCallback(
    debounce(async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoadingSuggestions(true);
      try {
        // Parse the query to extract restaurant and location
        const parsed = parseSearchQuery(searchQuery);
        const results = await frictionlessApi.getRestaurantAutocomplete(
          parsed.restaurant,
          parsed.location || undefined
        );
        console.log('🔍 Restaurant autocomplete results:', results);
        setSuggestions(results);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoadingSuggestions(false);
      }
    }, 300), // 300ms debounce
    []
  );

  useEffect(() => {
    fetchSuggestions(query);
  }, [query, fetchSuggestions]);

  const handleSearch = async (restaurantName?: string, restaurantLocation?: string, restaurantData?: any) => {
    const searchQuery = restaurantName || query;
    
    if (!searchQuery.trim()) {
      toast({
        title: 'Enter a restaurant name',
        description: 'Type the name of your restaurant to get started',
        variant: 'destructive'
      });
      return;
    }

    // Allow direct search even without selecting from suggestions
    // This enables users to search for restaurants not in the dropdown
    const finalRestaurantData = restaurantData || selectedRestaurant || null;

    // Parse the search query for restaurant and location
    const parsed = parseSearchQuery(searchQuery);
    const finalRestaurantName = restaurantName || parsed.restaurant;
    const finalLocation = restaurantLocation || parsed.location;

    setIsSearching(true);
    try {
      const response = await frictionlessApi.searchComplete({
        restaurant_name: finalRestaurantName,
        location: finalLocation,
        max_menus: 5,
        restaurant_data: finalRestaurantData  // Send full restaurant data
      });

      console.log('Full response object:', response);
      console.log('Response keys:', Object.keys(response));
      console.log('Search response:', {
        success: response.success,
        menusLength: response.menus?.length,
        hasRestaurant: !!response.restaurant,
        menus: response.menus
      });
      
      // Debug the condition
      console.log('Condition check:', {
        'response.success': response.success,
        'typeof response.success': typeof response.success,
        'response.menus exists': !!response.menus,
        'response.menus.length': response.menus?.length,
        'length > 0': response.menus?.length > 0,
        'full condition': response.success && response.menus && response.menus.length > 0
      });
      
      // Force navigation if we have menus (regardless of success flag)
      if (response.menus && response.menus.length > 0) {
        console.log('We have menus! Forcing navigation to results');
        // Include the full restaurant data with phone, rating, etc.
        const enrichedResponse = {
          ...response,
          restaurant: finalRestaurantData || response.restaurant
        };
        navigate('/restaurant-menu-results', {
          state: {
            searchResults: enrichedResponse,
            restaurantName: finalRestaurantName,
            location: finalLocation
          }
        });
        return; // Exit early
      }

      // UPDATED FLOW WITH needs_manual_upload FLAG:
      // 1. Check needs_manual_upload flag first (new backend flag)
      // 2. If we found PDFs online → Show them in results page
      // 3. If no PDFs (needs_manual_upload=true) → Go to upload
      // 4. If nothing found → Show error
      
      // Check the new needs_manual_upload flag from backend
      if (response.needs_manual_upload) {
        console.log('Backend indicates manual upload needed (no PDFs found)');
        navigate('/restaurant-menu-upload', {
          state: {
            restaurant_name: finalRestaurantName,
            location: finalLocation,
            restaurant_info: finalRestaurantData || response.restaurant,
            source: 'search',
            message: 'We couldn\'t find your menu online. Please upload your menu PDF.',
            searchAttempted: true
          }
        });
      } else if (response.success && response.menus && response.menus.length > 0) {
        console.log('Found PDFs online! Showing results - navigating to menu-results');
        // Include the full restaurant data with phone, rating, etc.
        const enrichedResponse = {
          ...response,
          restaurant: finalRestaurantData || response.restaurant
        };
        navigate('/restaurant-menu-results', {
          state: {
            searchResults: enrichedResponse,
            restaurantName: finalRestaurantName,
            location: finalLocation
          }
        });
      } else if (response.restaurant) {
        console.log('Found restaurant but no menus, going to upload');
        navigate('/restaurant-menu-upload', {
          state: {
            restaurant_name: finalRestaurantName,
            location: finalLocation,
            restaurant_info: finalRestaurantData || response.restaurant,
            source: 'search',
            message: 'We found your restaurant! Please upload your menu.'
          }
        });
      } else {
        console.log('Nothing found - redirecting to manual upload');
        // Instead of just showing a toast, redirect to upload page
        navigate('/restaurant-menu-upload', {
          state: {
            restaurant_name: finalRestaurantName,
            location: finalLocation,
            restaurant_info: null,
            source: 'search',
            message: `We couldn't find "${finalRestaurantName}" online. No worries! You can upload your menu directly.`,
            searchAttempted: true,
            notFound: true
          }
        });
      }
    } catch (error: any) {
      console.error('Search error:', error);
      // Even on error, redirect to upload page so user isn't stuck
      navigate('/restaurant-menu-upload', {
        state: {
          restaurant_name: finalRestaurantName,
          location: finalLocation,
          restaurant_info: null,
          source: 'search',
          message: 'Search encountered an issue. You can upload your menu directly.',
          searchAttempted: true,
          error: true
        }
      });
    } finally {
      setIsSearching(false);
    }
  };


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-dineai-blue/95 to-dineai-blue relative overflow-hidden">
      {/* Subtle animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-dineai-yellow/5 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full bg-white/5 blur-3xl animate-pulse delay-700"></div>
      </div>

      {/* Logo at top-left corner */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="absolute top-8 left-8 z-20"
      >
        <Logo />
      </motion.div>

      <div className="container mx-auto px-4 flex flex-col items-center min-h-screen relative z-10 pt-40">

        {/* Main Content - Top positioned */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-3xl"
        >
          {/* AI Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-dineai-yellow/10 px-4 py-2 rounded-full border border-dineai-yellow/20 mb-6">
              <Sparkles className="w-4 h-4 text-dineai-yellow" />
              <span className="text-dineai-yellow text-sm font-medium">AI-Powered Menu Search</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
              Find Your Restaurant's Menu
            </h1>
            <div className="space-y-1">
              <p className="text-white/90 text-lg flex items-center justify-center gap-2">
                <span className="text-xl">🚀</span>
                We'll search for your menu online and extract it in minutes!
              </p>
              <p className="text-white/70 text-base">
                Can't find it? No worries - just upload your PDF and we'll handle the rest.
              </p>
            </div>
          </motion.div>

          {/* Search Box - Large and Prominent */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className={`relative transition-all duration-300 ${
              isFocused ? 'transform scale-105' : ''
            }`}>
              {/* Search Input Container */}
              <div className="relative bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 hover:border-white/30 transition-all">
                <div className="flex items-center px-6 py-5">
                  <Search className={`w-6 h-6 mr-4 transition-colors ${
                    isFocused ? 'text-dineai-yellow' : 'text-white/50'
                  }`} />
                  
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setSelectedRestaurant(null); // Reset selection when user types
                    }}
                    onKeyPress={handleKeyPress}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                    placeholder="Enter restaurant name or 'Restaurant, Location'..."
                    className="flex-1 bg-transparent text-white text-xl placeholder:text-white/40 focus:outline-none"
                    disabled={isSearching}
                  />
                  
                  {query && (
                    <button
                      onClick={() => handleSearch()}
                      disabled={isSearching}
                      className="ml-4 bg-dineai-yellow hover:bg-yellow-500 text-black px-6 py-2 rounded-full font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                      {isSearching ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          Search
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Selected Restaurant Display */}
              {selectedRestaurant && !isFocused && (
                <div className="mt-4 px-6 py-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-dineai-yellow" />
                      <div>
                        <div className="text-white font-medium">{selectedRestaurant.name}</div>
                        <div className="text-white/60 text-sm">{selectedRestaurant.address}</div>
                        {selectedRestaurant.phone && (
                          <div className="text-white/50 text-xs mt-1">📞 {selectedRestaurant.phone}</div>
                        )}
                      </div>
                    </div>
                    {selectedRestaurant.rating && (
                      <div className="text-dineai-yellow text-sm font-medium">
                        ⭐ {selectedRestaurant.rating}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Suggestions Dropdown */}
              <AnimatePresence>
                {/* Google Places Autocomplete - shows real restaurants with addresses */}
                {isFocused && query.length > 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden z-50 max-h-96 overflow-y-auto"
                  >
                    {isLoadingSuggestions ? (
                      <div className="px-4 py-4 text-center text-white/50">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                        <div className="text-sm">Searching for restaurants...</div>
                      </div>
                    ) : suggestions.length > 0 ? (
                      <>
                        <div className="px-4 py-2 text-white/50 text-sm border-b border-white/10">
                          Suggested restaurants
                        </div>
                        {suggestions.map((restaurant, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              // Use the full restaurant name with address for better search
                              const searchName = restaurant.name;
                              // Use full address instead of just city
                              const searchLocation = restaurant.address || '';
                              setQuery(searchName);
                              setSelectedRestaurant(restaurant); // Store the selected restaurant

                              // Store restaurant info for pre-populating signup form
                              sessionStorage.setItem('restaurant_prefill_data', JSON.stringify({
                                name: restaurant.name,
                                address: restaurant.address,
                                phone: restaurant.phone,
                                cuisine_type: restaurant.cuisine_type,
                                website: restaurant.website,
                                rating: restaurant.rating,
                                price_level: restaurant.price_level
                              }));

                              // Pass the full restaurant data from Google Places
                              handleSearch(searchName, searchLocation, restaurant);
                            }}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/10 transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-dineai-yellow/20 rounded-lg flex items-center justify-center">
                                <MapPin className="w-5 h-5 text-dineai-yellow" />
                              </div>
                              <div className="text-left flex-1">
                                <div className="text-white font-medium">
                                  {restaurant.name}
                                </div>
                                <div className="text-white/60 text-sm">
                                  {restaurant.address && (
                                    <span className="line-clamp-1">{restaurant.address}</span>
                                  )}
                                </div>
                                <div className="text-white/40 text-xs flex items-center gap-3 mt-1">
                                  {restaurant.rating && (
                                    <span className="flex items-center gap-1">
                                      <Star className="w-3 h-3 fill-current text-dineai-yellow" />
                                      {restaurant.rating}
                                    </span>
                                  )}
                                  {restaurant.phone && (
                                    <span>{restaurant.phone}</span>
                                  )}
                                  {(restaurant.type === 'restaurant' || (restaurant.types && restaurant.types.includes('restaurant'))) && (
                                    <span className="text-dineai-yellow/60">Restaurant</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors" />
                          </button>
                        ))}
                      </>
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>


        </motion.div>

        {/* Bottom Section - Fixed at bottom with padding for chat button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="absolute bottom-12 left-0 right-0 text-center px-4 md:px-20"
        >
          {/* Community CTA */}
          <p className="text-white/90 text-base md:text-lg font-medium flex items-center justify-center gap-2 flex-wrap">
            <span className="text-xl">🔥</span>
            <span>Join restaurants transforming their business in days, not months</span>
          </p>
          <p className="text-white/70 text-sm md:text-base mt-1">
            The fastest-growing restaurant community
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RestaurantMenuSearch;